/**
 * The layers tree (front-most first): select (Shift/Mod for more), rename (double-click or F2), reorder and
 * re-parent by drag and drop, collapse groups, hide and lock. A WAI-ARIA tree with a roving tab stop: arrows move,
 * Left/Right collapse/expand, Enter renames, Delete deletes.
 */
import { memo, useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type MutableRefObject } from 'react';
import { ChevronDown, ChevronRight, Circle, Eye, EyeOff, Film, Image, Lock, LockOpen, Minus, RectangleHorizontal, Rows3, Square, SquareDashed, Type } from 'lucide-react';
import type { Element } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';
import { patchElement, reparent } from '../model/commands.ts';
import { findElement, flattenForLayers, indexIn, isContainer, parentOf, pathTo } from '../model/tree.ts';
import { deleteSelection, toggleLocked, toggleVisible } from '../state/actions.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { isMac } from '../shortcuts.ts';

function TypeIcon({ el }: { el: Element }) {
  const p = { size: 14, 'aria-hidden': true };
  switch (el.type) {
    case 'text':
      return <Type {...p} />;
    case 'rect':
      return <Square {...p} />;
    case 'ellipse':
      return <Circle {...p} />;
    case 'line':
      return <Minus {...p} />;
    case 'image':
      return <Image {...p} />;
    case 'video':
      return <Film {...p} />;
    case 'clipMask':
      return <SquareDashed {...p} />;
    case 'group':
      return el.layout.mode === 'stack' ? (el.style.fill ? <RectangleHorizontal {...p} /> : <Rows3 {...p} />) : <SquareDashed {...p} />;
  }
}

type DropPos = 'before' | 'after' | 'inside';

export function LayersPanel({ store }: { store: EditorStore }) {
  const scene = useEditor(store, sceneView);
  const selection = useEditor(store, (s) => s.selection);
  const collapsedList = useEditor(store, (s) => s.ui.collapsed);
  const collapsed = new Set(collapsedList);
  const rows = flattenForLayers(scene, collapsed);
  const [focusId, setFocusId] = useState<string>();
  const [renaming, setRenaming] = useState<string>();
  const [drop, setDrop] = useState<{ id: string; pos: DropPos }>();
  const dragId = useRef<string | undefined>(undefined);
  const treeRef = useRef<HTMLUListElement>(null);

  const current = focusId && rows.some((r) => r.el.id === focusId) ? focusId : (selection[0] ?? rows[0]?.el.id);

  useEffect(() => {
    // Keep the selected row visible.
    const id = selection[selection.length - 1];
    if (id) treeRef.current?.querySelector(`[data-element-id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selection]);

  const focusRow = (id: string) => {
    setFocusId(id);
    requestAnimationFrame(() => (treeRef.current?.querySelector(`[data-element-id="${CSS.escape(id)}"]`) as HTMLElement | null)?.focus());
  };

  const setCollapsed = (id: string, value: boolean) => {
    const next = new Set(collapsed);
    if (value) next.add(id);
    else next.delete(id);
    store.setUi({ collapsed: [...next] });
  };

  const onClickRow = (e: React.MouseEvent, id: string) => {
    setFocusId(id);
    const multi = isMac ? e.metaKey : e.ctrlKey;
    if (e.shiftKey && selection.length) {
      const ids = rows.map((r) => r.el.id);
      const a = ids.indexOf(selection[selection.length - 1]!);
      const b = ids.indexOf(id);
      const range = ids.slice(Math.min(a, b), Math.max(a, b) + 1).filter((x) => parentOf(scene, x)?.id === parentOf(scene, id)?.id);
      store.select([...new Set([...selection, ...range])]);
    } else if (multi) store.select(selection.includes(id) ? selection.filter((s) => s !== id) : [...selection, id]);
    else store.select([id]);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLLIElement>, id: string) => {
    if (renaming) return;
    const ids = rows.map((r) => r.el.id);
    const i = ids.indexOf(id);
    const el = findElement(scene, id)!;
    const move = (j: number) => {
      const next = ids[Math.max(0, Math.min(ids.length - 1, j))];
      if (!next) return;
      focusRow(next);
      if (e.shiftKey) store.select([...new Set([...selection, next])]);
      else store.select([next]);
    };
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(i + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(ids.length - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (isContainer(el) && collapsed.has(id)) setCollapsed(id, false);
        else if (isContainer(el) && el.children.length) move(i + 1);
        break;
      case 'ArrowLeft': {
        e.preventDefault();
        if (isContainer(el) && !collapsed.has(id) && el.children.length) setCollapsed(id, true);
        else {
          const p = parentOf(scene, id);
          if (p) {
            focusRow(p.id);
            store.select([p.id]);
          }
        }
        break;
      }
      case 'F2':
      case 'Enter':
        e.preventDefault();
        setRenaming(id);
        break;
      case ' ':
        e.preventDefault();
        store.select(selection.includes(id) ? selection.filter((s) => s !== id) : [...selection, id]);
        break;
    }
  };

  // Drag and drop: above a row = in front of it, below = behind it, the middle of a container = inside it.
  const posFor = (e: DragEvent, el: Element): DropPos => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const f = (e.clientY - r.top) / r.height;
    if (isContainer(el)) return f < 0.25 ? 'before' : f > 0.75 ? 'after' : 'inside';
    return f < 0.5 ? 'before' : 'after';
  };

  const onDrop = (target: Element, pos: DropPos) => {
    const id = dragId.current;
    dragId.current = undefined;
    setDrop(undefined);
    if (!id || id === target.id || pathTo(scene, target.id).includes(id)) return;
    // In every layout, each keeping the element where it is on screen there.
    store.applyEach((s, geo) => {
      if (pos === 'inside') return reparent(s, id, target.id, (findElement(s, target.id) as { children: Element[] }).children.length, geo);
      const parentId = parentOf(s, target.id)?.id ?? null;
      const idx = indexIn(s, target.id);
      return reparent(s, id, parentId, pos === 'before' ? idx + 1 : idx, geo);
    }, { select: [id] });
  };

  // Rows are memoised (a large scene re-renders only the rows whose element or state changed); they reach the
  // panel's current handlers through this ref.
  const handlers = useRef<RowHandlers>(undefined as unknown as RowHandlers);
  handlers.current = { store, onClickRow, onKeyDown, onDrop, posFor, setDrop, setRenaming, setCollapsed, focusRow, dragId };

  return (
    <div className="layers">
      {rows.length === 0 && <p className="empty">{t('layers.empty')}</p>}
      <ul className="tree" role="tree" aria-label={t('layers.label')} aria-multiselectable="true" ref={treeRef} data-testid="layers-tree">
        {rows.map(({ el, depth }) => {
          const container = isContainer(el);
          return (
            <LayerRow
              key={el.id}
              el={el}
              depth={depth}
              selected={selection.includes(el.id)}
              container={container}
              open={container && !collapsed.has(el.id)}
              dropHere={drop?.id === el.id ? drop.pos : undefined}
              tabbable={el.id === current}
              renaming={renaming === el.id}
              h={handlers}
            />
          );
        })}
      </ul>
      <p className="hint">{t('layers.hint')}</p>
      <button type="button" className="sr-only" onClick={() => deleteSelection(store)}>
        {t('layers.deleteSelected')}
      </button>
    </div>
  );
}

interface RowHandlers {
  store: EditorStore;
  onClickRow(e: React.MouseEvent, id: string): void;
  onKeyDown(e: KeyboardEvent<HTMLLIElement>, id: string): void;
  onDrop(target: Element, pos: DropPos): void;
  posFor(e: DragEvent, el: Element): DropPos;
  setDrop(f: (d: { id: string; pos: DropPos } | undefined) => { id: string; pos: DropPos } | undefined): void;
  setRenaming(id: string | undefined): void;
  setCollapsed(id: string, value: boolean): void;
  focusRow(id: string): void;
  dragId: MutableRefObject<string | undefined>;
}

/** One row of the layers tree; re-renders only when its element (immutable) or its own state changes. */
const LayerRow = memo(function LayerRow({ el, depth, selected, container, open, dropHere, tabbable, renaming, h }: {
  el: Element;
  depth: number;
  selected: boolean;
  container: boolean;
  open: boolean;
  dropHere: DropPos | undefined;
  tabbable: boolean;
  renaming: boolean;
  h: MutableRefObject<RowHandlers>;
}) {
  return (
    <li
      role="treeitem"
      aria-level={depth + 1}
      aria-selected={selected}
      aria-expanded={container ? open : undefined}
      aria-label={`${el.name || el.id}${el.visible ? '' : `, ${t('layers.hidden')}`}${el.locked ? `, ${t('layers.locked')}` : ''}`}
      tabIndex={tabbable ? 0 : -1}
      data-element-id={el.id}
      data-testid="layer-row"
      className={`tree-row${selected ? ' selected' : ''}${el.visible ? '' : ' is-hidden'}${dropHere ? ` drop-${dropHere}` : ''}`}
      style={{ paddingLeft: 6 + depth * 14 }}
      draggable={!renaming}
      onDragStart={(e) => {
        h.current.dragId.current = el.id;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/x-prism-layer', el.id);
      }}
      onDragOver={(e) => {
        if (!h.current.dragId.current) return;
        e.preventDefault();
        const pos = h.current.posFor(e, el);
        h.current.setDrop(() => ({ id: el.id, pos }));
      }}
      onDragLeave={() => h.current.setDrop((d) => (d?.id === el.id ? undefined : d))}
      onDrop={(e) => {
        e.preventDefault();
        h.current.onDrop(el, h.current.posFor(e, el));
      }}
      onDragEnd={() => {
        h.current.dragId.current = undefined;
        h.current.setDrop(() => undefined);
      }}
      onClick={(e) => h.current.onClickRow(e, el.id)}
      onDoubleClick={() => h.current.setRenaming(el.id)}
      onKeyDown={(e) => h.current.onKeyDown(e, el.id)}
    >
      <span
        className="twisty"
        onClick={(e) => {
          e.stopPropagation();
          if (container) h.current.setCollapsed(el.id, open);
        }}
        aria-hidden
      >
        {container ? open ? <ChevronDown size={12} /> : <ChevronRight size={12} /> : null}
      </span>
      <TypeIcon el={el} />
      {renaming ? (
        <input
          className="rename"
          autoFocus
          defaultValue={el.name}
          aria-label={t('layers.rename')}
          data-testid="layer-rename"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              h.current.setRenaming(undefined);
              h.current.focusRow(el.id);
            }
          }}
          onBlur={(e) => {
            const name = e.target.value.trim();
            h.current.setRenaming(undefined);
            if (name && name !== el.name) h.current.store.apply((s) => patchElement(s, el.id, { name }));
            h.current.focusRow(el.id);
          }}
        />
      ) : (
        <span className="tree-name">{el.name || el.id}</span>
      )}
      <span className="tree-actions">
        <button
          type="button"
          className={`mini${el.locked ? ' on' : ''}`}
          tabIndex={-1}
          aria-label={el.locked ? t('layers.unlock') : t('layers.lock')}
          title={el.locked ? t('layers.unlock') : t('layers.lock')}
          onClick={(e) => {
            e.stopPropagation();
            toggleLocked(h.current.store, [el.id]);
          }}
        >
          {el.locked ? <Lock size={12} /> : <LockOpen size={12} />}
        </button>
        <button
          type="button"
          className={`mini${el.visible ? '' : ' on'}`}
          tabIndex={-1}
          aria-label={el.visible ? t('layers.hide') : t('layers.show')}
          title={el.visible ? t('layers.hide') : t('layers.show')}
          data-testid="layer-visibility"
          onClick={(e) => {
            e.stopPropagation();
            toggleVisible(h.current.store, [el.id]);
          }}
        >
          {el.visible ? <Eye size={12} /> : <EyeOff size={12} />}
        </button>
      </span>
    </li>
  );
});
